import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { fbt } from 'fbtee'
import { ImagePlus, X } from 'lucide-react'
import { MAX_FEEDBACK_ATTACHMENTS } from '@/lib/feedbackAttachments'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

function AttachmentTile({ children, onRemove }: { children: ReactNode; onRemove: () => void }) {
  return (
    <div className="relative size-16 overflow-hidden rounded-md border border-border bg-muted">
      {children}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-0.5 right-0.5 flex size-5 cursor-pointer items-center justify-center rounded-full bg-background/80 text-foreground"
        aria-label={fbt('Remove attachment', 'Accessible label for the button that removes a photo from a feedback report')}
      >
        <X className="size-3" />
      </button>
    </div>
  )
}

interface FeedbackAttachmentsProps {
  attachments: Blob[]
  onChange: (attachments: Blob[]) => void
  /** A shake screenshot still rendering: shown as a placeholder tile. */
  pending?: boolean
  onDismissPending?: () => void
}

/** Thumbnail strip of a feedback report's screenshots/photos, with add and remove. */
export function FeedbackAttachments({ attachments, onChange, pending, onDismissPending }: FeedbackAttachmentsProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const previews = useMemo(() => attachments.map(blob => URL.createObjectURL(blob)), [attachments])
  useEffect(() => () => previews.forEach(url => URL.revokeObjectURL(url)), [previews])

  // The pending screenshot holds a slot so it can't be pushed out when it lands.
  const slots = MAX_FEEDBACK_ATTACHMENTS - (pending ? 1 : 0)

  function addFiles(files: FileList | null) {
    const images = [...(files ?? [])].filter(f => f.type.startsWith('image/'))
    if (images.length) onChange([...attachments, ...images].slice(0, slots))
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {pending && (
        <AttachmentTile onRemove={() => onDismissPending?.()}>
          <Skeleton className="size-full rounded-none" />
        </AttachmentTile>
      )}
      {previews.map((url, i) => (
        <AttachmentTile key={url} onRemove={() => onChange(attachments.filter((_, j) => j !== i))}>
          <img src={url} alt="" className="size-full object-cover object-top" />
        </AttachmentTile>
      ))}
      {attachments.length < slots && (
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          className="size-16 rounded-md"
          aria-label={fbt('Add photo', 'Button in the feedback form that lets the user attach photos or screenshots')}
        >
          <ImagePlus className="size-5" />
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={e => addFiles(e.target.files)}
      />
    </div>
  )
}
